import React, { useCallback, useState } from 'react'
import { MainBody, WhiteBox, Main, MainTop } from '@/components/Layout'
import { LookupCell, createLookupFilterCell, DateCell, DateFilterCell } from '@/components/BsrsGrid'
import { GridColumn as Column } from '@progress/kendo-react-grid'
import { FormLoader, PropertyRenderer } from '@/components/changeRequests'
import context from '@/context'
import { ValidationBoundary } from '@/components/validation'
import { TabNavItems, TabContent, TabPane } from '@/components/tabs'
import { dialogTypes } from '@/dialogs'
import { AddressesIndividual } from '@/components/demographics/addresses'
import { Contacts } from '@/components/demographics/contacts'
import { IndividualGovernmentService } from '@/components/demographics/governmentService'
import { Row, Col, Alert } from 'reactstrap'
import { ScopeDetailHeader } from '@/components/changeRequests/scopeDetailHeader'
import { getBadges } from '@/components/changeRequests/badgeUtils'
import { useLocalization } from '@/hooks/localization'
import { useReduxState } from '@/hooks/reduxState'
import Icon from '@/components/Icon'
import { UserAccountInfo } from '@/internal-app/pages/individuals/userAccountInfo'
import { PersonalData } from '@/components/demographics/personalData'

function IndividualDetail (props) {
  const [activeTab, setActiveTab] = useState('1')
  const { institutionId, institutionName, institutionLatinName } = useReduxState(useCallback(state => ({
    institutionId: state.identity.user && state.identity.user.profile.InstitutionId,
    institutionName: state.identity.user && state.identity.user.profile.InstitutionName,
    institutionLatinName: state.identity.user && state.identity.user.profile.InstitutionLatinName
  }), []))

  const institution = { id: institutionId, name: institutionName, latinName: institutionLatinName }

  function toggle (tab) {
    if (activeTab !== tab) {
      setActiveTab(tab)
    }
  }
  const loader = useCallback(id => context.api.individualService.getIndividualById(id), [])
  return (
    <FormLoader {...{ match: props.match, loader, scope: 'Individuals' }} render={(formData) =>
      <IndividualDetailForm {...props} {...formData} activeTab={activeTab} toggle={toggle} institution={institution} />
    } />
  )
}

function IndividualDetailForm ({ activeTab, toggle, targetId, mode, data, institution }) {
  const { localize } = useLocalization()

  function loadPersonalIds (req) {
    return context.api.individualService.getPersonalIds(targetId, req)
  }

  function loadAddresses (req) {
    return context.api.individualService.getAddresses(targetId, req)
  }

  function loadContacts (req) {
    return context.api.individualService.getContactInformation(targetId, req)
  }

  function loadRelatedPersons (req) {
    return context.api.individualService.getRelatedPersons(targetId, req)
  }

  return (
    <Main path={[{ text: <PropertyRenderer name='name' readonly /> }]}>
      <MainTop>
        <ScopeDetailHeader title='Individuals.DetailTitle' backUrl='/demographics/individuals'>
          <PropertyRenderer name='name' readonly />
        </ScopeDetailHeader>
      </MainTop>
      <MainBody transparent>
        <ValidationBoundary>
          <Row>
            <Col lg='8' className='white-box-full-height'>
              <PersonalData />
            </Col>
            <Col lg='4' className='white-box-full-height'>
              {targetId && <UserAccountInfo id={targetId} />}
              <WhiteBox header='Individuals.Description'>
                <PropertyRenderer name='description' inputType='textarea' />
              </WhiteBox>
            </Col>
          </Row>
          <Row>
            <Col>
              <WhiteBox>
                <TabNavItems
                  buttonView
                  activeTabId={activeTab}
                  onClick={toggle}
                  items={getBadges(mode, data, [
                    { id: '1', text: 'Individuals.PersonalIds', badge: 'personalIds' },
                    { id: '2', text: 'Persons.Addresses', badge: 'addresses' },
                    { id: '3', text: 'Persons.Contacts', badge: 'contacts' },
                    { id: '6', text: 'Individuals.RelatedInstitutions', badge: 'relatedPersons' },
                    { id: '7', text: 'Individuals.GovernmentService', badge: 'governmentService' }
                  ])} />
                <TabContent activeTab={activeTab}>
                  <TabPane tabId='1'>
                    <PropertyRenderer
                      name='personalIds' inputType='grid'
                      addButtonLabel='Individuals.AddPersonalId'
                      detailDialog={{ dialogType: dialogTypes.personalId }}
                      dataSource={loadPersonalIds}>
                      <Column field='type' title={localize('Individuals.PersonalIdType')} cell={LookupCell} width='200px'
                        filterCell={createLookupFilterCell(context.cache.getPersonalIdTypes)} />
                      <Column field='number' title={localize('Individuals.PersonalIdNumber')} />
                      <Column field='validFrom' title={localize('Common.ValidFrom')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                      <Column field='validTo' title={localize('Common.ValidTo')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                    </PropertyRenderer>
                  </TabPane>
                  <TabPane tabId='2'>
                    <AddressesIndividual dataSource={loadAddresses} localize={localize} />
                  </TabPane>
                  <TabPane tabId='3'>
                    <Contacts dataSource={loadContacts} localize={localize} />
                  </TabPane>
                  <TabPane tabId='6'>
                    {context.isReportApp() && <Alert color='warning'><Icon name='error-circle' /> {localize('Individuals.AssociationRequired')}</Alert>}
                    <PropertyRenderer
                      name='relatedPersons' inputType='grid'
                      addButtonLabel='Individuals.AddAssociation'
                      detailDialog={{ dialogType: dialogTypes.relatedInstitution }}
                      defaultValue={{ institution: institution }}
                      dataSource={loadRelatedPersons}>
                      <Column field='relatedPersonType' title={localize('Individuals.RelatedPersonType')} cell={LookupCell} width='200px' />
                      <Column field='validFrom' title={localize('Common.ValidFrom')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                      <Column field='validTo' title={localize('Common.ValidTo')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                    </PropertyRenderer>
                  </TabPane>
                  <TabPane tabId='7'>
                    <IndividualGovernmentService />
                  </TabPane>
                </TabContent>
              </WhiteBox>
            </Col>
          </Row>
        </ValidationBoundary>
      </MainBody>
    </Main>
  )
}

export {
  IndividualDetail
}



// WEBPACK FOOTER //
// ./src/report-app/pages/individuals/detail.js